import Matter from "matter-js";

export class CoinsGravity {
  constructor() {
    this.isInitialized = false;
    this.gravities = new Map();
    this.prefersReducedMotion = false;
  }

  init() {
    if (this.isInitialized) return;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth <= 768;

    this.initGravities(isMobile);
    this.isInitialized = true;
  }

  initGravities(isMobile) {
    const containers = document.querySelectorAll('[data-gravity]');

    containers.forEach((container, index) => {
      if (this.gravities.has(container)) {
        this.destroyGravity(container);
      }

      const gravity = new GravityInstance(container, index, {
        prefersReducedMotion: this.prefersReducedMotion,
        isMobile: isMobile
      });
      this.gravities.set(container, gravity);
    });
  }

  triggerJump(force) {
    this.gravities.forEach((gravity) => {
      gravity.jump(force);
    });
  }

  destroyGravity(container) {
    const gravity = this.gravities.get(container);
    if (gravity) {
      gravity.destroy();
      this.gravities.delete(container);
    }
  }

  destroy() {
    this.gravities.forEach((gravity, container) => {
      this.destroyGravity(container);
    });

    this.isInitialized = false;
  }
}

class GravityInstance {
  constructor(container, index, options = {}) {
    this.container = container;
    this.index = index;
    this.options = options;

    this.engine = null;
    this.render = null;
    this.runner = null;
    this.world = null;
    this.mouse = null;
    this.mouseConstraint = null;
    this.boundaries = [];
    this.coinBodies = [];

    this.oldWidth = 0;
    this.oldHeight = 0;
    this.isVisible = false;
    this.hasInitialized = false;

    // Event handlers (need to be bound for cleanup)
    this.handleResize = this.handleResize.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);

    this.click = false;

    // Configuration
    this.config = this.getConfiguration();

    // Setup intersection observer
    this.setupIntersectionObserver();
  }

  getConfiguration() {
    const gravityX = parseFloat(this.container.getAttribute('data-gravity-x')) || 0;
    const gravityY = parseFloat(this.container.getAttribute('data-gravity-y')) || 2;
    const duplicationFactor = parseFloat(this.container.getAttribute('data-duplication-factor')) || 2.5;

    return {
      gravityX,
      gravityY,
      duplicationFactor,
      baseCoins: this.getBaseCoins()
    };
  }

  getBaseCoins() {
    const isMobile = this.options.isMobile || false;

    // Different sizes for mobile vs desktop
    const tbtcSize = isMobile ? 100 : 144;
    const btcSize = isMobile ? 102 : 146;

    return [
      this.createCoin(
        "TBTC",
        tbtcSize,
        tbtcSize,
        "https://cdn.prod.website-files.com/68da6f24db5fedbdab2e7d56/68dc0f20cc3fcd96decab7f7_tbtc.svg"
      ),
      this.createCoin(
        "BTC",
        btcSize,
        btcSize,
        "https://cdn.prod.website-files.com/68da6f24db5fedbdab2e7d56/68dc0dca7fd5ce90b30e8acb_btc.svg"
      )
    ];
  }

  createCoin(text, width, height, url) {
    return { text, width, height, url };
  }

  setupIntersectionObserver() {
    // Configure observer to trigger when element is in viewport
    const observerOptions = {
      root: null, // viewport
      rootMargin: '0px', // Trigger slightly before entering viewport
      threshold: 0.4 // Trigger when 40% visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.hasInitialized) {
          // Element is visible, start gravity
          this.isVisible = true;
          this.init();
          this.hasInitialized = true;
        } else if (entry.isIntersecting && this.hasInitialized) {
          // Resume if paused
          this.isVisible = true;
          this.resume();
        } else {
          // Element not visible, pause to save resources
          this.isVisible = false;
          this.pause();
        }
      });
    }, observerOptions);

    this.observer.observe(this.container);
  }

  pause() {
    if (this.runner && this.engine) {
      this.runner.enabled = false;
    }
  }

  resume() {
    if (this.runner && this.engine) {
      this.runner.enabled = true;
    }
  }

  init() {
    if (this.options.prefersReducedMotion) {
      return;
    }

    const {
      Engine,
      Render,
      MouseConstraint,
      Mouse,
      World,
      Bodies,
      Runner,
    } = Matter;

    // Create engine
    this.engine = Engine.create();
    this.world = this.engine.world;
    this.engine.gravity.x = this.config.gravityX;
    this.engine.gravity.y = this.config.gravityY;

    // Add engine constraints for stability
    this.engine.positionIterations = 10; // More accurate collisions
    this.engine.velocityIterations = 8;
    this.engine.constraintIterations = 4;

    // Get container dimensions
    let containerWidth = this.container.clientWidth;
    let containerHeight = this.container.clientHeight;

    if (containerWidth === 0 || containerHeight === 0) {
      console.error('Container has no dimensions!');
      return;
    }

    this.oldWidth = containerWidth;
    this.oldHeight = containerHeight;

    // Create renderer
    this.render = Render.create({
      element: this.container,
      engine: this.engine,
      options: {
        width: containerWidth,
        height: containerHeight,
        pixelRatio: 2,
        background: "transparent",
        wireframes: false,
      },
    });

    // Create boundaries
    this.createBoundaries();

    // Prepare coins
    const coins = this.prepareCoins();

    // Preload images and create bodies
    this.preloadImages(coins)
      .then(() => {
        this.createCoinBodies(coins);

        // Start gravity - CREATE runner first, then run it
        this.runner = Runner.create();
        Runner.run(this.runner, this.engine);
        Render.run(this.render);
      })
      .catch((error) => {
        console.error("Failed to load images:", error);
      });

    // Setup mouse interaction
    this.setupMouseInteraction();

    // Setup resize handler
    window.addEventListener("resize", this.handleResize);

    // Setup click detection
    document.addEventListener("mousedown", this.onMouseDown);
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);
  }

  createBoundaries() {
    const { World, Bodies } = Matter;
    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;

    // Remove old boundaries
    this.boundaries.forEach((body) => World.remove(this.world, body));
    this.boundaries = [];

    const createStaticBoundary = (x, y, width, height, options = {}) => {
      return Bodies.rectangle(x, y, width, height, {
        isStatic: true,
        restitution: 0.3,
        friction: 0.1,
        ...options,
      });
    };

    // Create THICK boundaries that definitely contain the bodies
    const thickness = 100;

    // Ground - at the very bottom
    const ground = createStaticBoundary(
      containerWidth / 2,
      containerHeight + thickness / 2,
      containerWidth + thickness * 2,
      thickness,
      { render: { fillStyle: "transparent" } }
    );

    // Left wall
    const wallLeft = createStaticBoundary(
      -thickness / 2,
      containerHeight / 2,
      thickness,
      containerHeight + thickness * 2,
      { render: { fillStyle: "transparent" } }
    );

    // Right wall
    const wallRight = createStaticBoundary(
      containerWidth + thickness / 2,
      containerHeight / 2,
      thickness,
      containerHeight + thickness * 2,
      { render: { fillStyle: "transparent" } }
    );

    // Ceiling - at the very top
    const roof = createStaticBoundary(
      containerWidth / 2,
      -thickness / 2,
      containerWidth + thickness * 2,
      thickness,
      { render: { fillStyle: "transparent" } }
    );

    this.boundaries.push(ground, wallLeft, wallRight, roof);
    World.add(this.world, this.boundaries);
  }

  prepareCoins() {
    const coins = [];
    for (let i = 0; i < this.config.duplicationFactor; i++) {
      this.config.baseCoins.forEach((coin) => {
        coins.push({ ...coin });
      });
    }
    return coins;
  }

  preloadImages(coins) {
    return Promise.all(
      coins.map(
        (coin) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => {
              console.error(`Failed to load image: ${coin.url}`);
              reject(new Error(`Image load error for ${coin.url}`));
            };
            img.src = coin.url;
          })
      )
    );
  }

  createCoinBodies(coins) {
    const { World, Bodies } = Matter;
    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;

    this.coinBodies = coins.map((coin) => {
      const position = this.getRandomPosition(containerWidth, containerHeight / 2);
      const velocity = this.getRandomVelocity();

      // Calculate scale based on desired size vs natural SVG size
      // Assuming SVGs are naturally 144x144 (desktop size)
      const naturalSize = 144;
      const scale = coin.width / naturalSize;

      const body = Bodies.rectangle(position.x, position.y, coin.width, coin.height, {
        chamfer: { radius: 20 },
        render: {
          sprite: {
            texture: coin.url,
            xScale: scale,
            yScale: scale
          }
        },
        restitution: 0.6, // Bounce
        friction: 0.05,
        frictionAir: 0.02, // Air resistance to slow things down
        density: 0.001, // Make them lighter
        slop: 0.05, // Collision tolerance
      });

      // Set initial velocity
      Matter.Body.setVelocity(body, velocity);

      return body;
    });

    World.add(this.world, this.coinBodies);
  }

  getRandomPosition(maxWidth, maxHeight) {
    return {
      x: Math.random() * maxWidth,
      y: Math.random() * maxHeight,
    };
  }

  getRandomVelocity() {
    return {
      x: (Math.random() - 0.5) * 4, // Reduced from 10 to 4
      y: (Math.random() - 0.5) * 4,
    };
  }

  setupMouseInteraction() {
    const { MouseConstraint, Mouse, World } = Matter;

    this.mouse = Mouse.create(this.render.canvas);
    this.mouseConstraint = MouseConstraint.create(this.engine, {
      mouse: this.mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false },
      },
    });
    World.add(this.world, this.mouseConstraint);
    this.render.mouse = this.mouse;

    // Remove default matter wheel events
    this.removeDefaultMouseEvents();
  }

  removeDefaultMouseEvents() {
    if (this.mouse && this.mouse.element) {
      this.mouse.element.removeEventListener("mousewheel", this.mouse.mousewheel);
      this.mouse.element.removeEventListener("DOMMouseScroll", this.mouse.mousewheel);
    }
  }

  onMouseDown() {
    this.click = true;
  }

  onMouseMove() {
    this.click = false;
  }

  onMouseUp() {
  }

  handleResize() {
    this.resizeCanvas();
    this.scaleBodiesToFit();
    this.createBoundaries();
  }

  resizeCanvas() {
    this.oldWidth = this.render.options.width;
    this.oldHeight = this.render.options.height;

    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;

    this.render.canvas.width = containerWidth * this.render.options.pixelRatio;
    this.render.canvas.height = containerHeight * this.render.options.pixelRatio;
    this.render.canvas.style.width = `${containerWidth}px`;
    this.render.canvas.style.height = `${containerHeight}px`;
    this.render.options.width = containerWidth;
    this.render.options.height = containerHeight;
  }

  scaleBodiesToFit() {
    const { Body } = Matter;

    if (!this.oldWidth || !this.oldHeight) return;

    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;
    const scaleX = containerWidth / this.oldWidth;
    const scaleY = containerHeight / this.oldHeight;

    this.coinBodies.forEach((body) => {
      Body.scale(body, scaleX, scaleY);
      const newX = body.position.x * scaleX;
      const newY = body.position.y * scaleY;
      Body.setPosition(body, { x: newX, y: newY });
    });
  }

  jump(force = 15) {
    if (!this.engine || !this.coinBodies.length) return;

    const { Body } = Matter;

    this.coinBodies.forEach((body) => {
      // Apply upward force with some random horizontal variation
      const randomX = (Math.random() - 0.5) * 15;
      Body.setVelocity(body, {
        x: body.velocity.x + randomX,
        y: -force // Negative is upward
      });
    });
  }

  destroy() {
    const { World, Render, Runner, Engine } = Matter;

    // Disconnect intersection observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove event listeners
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("mousedown", this.onMouseDown);
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);

    // Remove mouse events
    this.removeDefaultMouseEvents();

    // Stop runner
    if (this.runner) {
      Runner.stop(this.runner);
      this.runner = null;
    }

    // Stop render
    if (this.render) {
      Render.stop(this.render);
      if (this.render.canvas) {
        this.render.canvas.remove();
      }
      this.render = null;
    }

    // Clear world
    if (this.world) {
      World.clear(this.world, false);
      this.world = null;
    }

    // Clear engine
    if (this.engine) {
      Engine.clear(this.engine);
      this.engine = null;
    }

    // Clear references
    this.coinBodies = [];
    this.boundaries = [];
    this.mouse = null;
    this.mouseConstraint = null;
  }
}